import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { workingDraftStorage } from './workingDraftStorage';
import { z } from 'zod';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { recordHostedInteraction } from '@/lib/studyToolTelemetry';
import { getKidSub, useAuthStore } from '@/auth/authStore';
import { STUDY_TOOL_CACHE_PREFIX } from '@/auth/studyToolCache';
import { MathWorkingCanvas } from './MathWorkingCanvas';
import { useWorkingTutorConversation } from './workingTutorConversation';
import { forgetWorkingTutorReturn } from './workingTutorReturnCache';
import { WorkingTutorPanel } from './WorkingTutorPanel';
import {
  blankWorking,
  workingContentSchema,
  type WorkingContent,
  type WorkingContext,
  type WorkingRow,
} from './workingCanvas';

const pendingSchema = z
  .object({
    context: z
      .object({
        kind: z.enum(['practice', 'study', 'mock', 'free', 'material']),
        question_id: z.string(),
        session_id: z.string().optional(),
      })
      .strict(),
    content: workingContentSchema,
    expected_version: z.number().int().nonnegative(),
    request_id: z.string().uuid(),
  })
  .strict();
type PendingSave = z.infer<typeof pendingSchema>;
interface Draft {
  id: string;
  version: number;
  content: WorkingContent;
  dirty: boolean;
  pending: PendingSave | null;
}
export interface WorkingCanvasHandle {
  flush: () => Promise<string | null>;
  hasUnsavedChanges?: () => boolean;
}
interface Props {
  context: WorkingContext;
  savedPageId?: string;
  question?: string | null;
  contextLabel?: string;
  locked?: boolean;
  disabled?: boolean;
  onSaved?: (id: string) => void;
  startOpen?: boolean;
  startWithTutor?: boolean;
  embedded?: boolean;
}
const path = (id: string) => `/study/working-canvases/${encodeURIComponent(id)}`;

// Context changes must remount this component. Drafts use the existing logout-cleared, learner-scoped cache.
export const WorkingCanvasPanel = forwardRef<WorkingCanvasHandle, Props>(
  function WorkingCanvasPanel(
    {
      context,
      savedPageId,
      question,
      contextLabel,
      locked = false,
      disabled = false,
      onSaved,
      startOpen,
      startWithTutor,
      embedded,
    },
    ref,
  ) {
    const navigate = useNavigate();
    const [owner] = useState(getKidSub);
    const key = `${STUDY_TOOL_CACHE_PREFIX}working:${owner ?? 'unknown'}:${JSON.stringify(context)}`;
    const [content, setContent] = useState<WorkingContent>(blankWorking);
    const [status, setStatus] = useState('Loading working…');
    const [ready, setReady] = useState(false);
    const [serverLocked, setServerLocked] = useState(false);
    const [conflict, setConflict] = useState(false);
    const [cacheFailed, setCacheFailed] = useState(false);
    const [confirmReload, setConfirmReload] = useState(false);
    const [recovery, setRecovery] = useState<WorkingContent | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const draft = useRef<Draft>({
      id: crypto.randomUUID(),
      version: 0,
      content: blankWorking(),
      dirty: false,
      pending: null,
    });
    const conversation = useWorkingTutorConversation(
      undefined,
      `working:${savedPageId ?? draft.current.id}`,
    );
    const inFlight = useRef<Promise<string | null> | null>(null);
    const mounted = useRef(true);
    const requests = useRef(new AbortController());
    const current = () => mounted.current && Boolean(owner) && getKidSub() === owner;
    const loaded = useRef(false);
    const propsRef = useRef({ context, locked, onSaved });
    propsRef.current = { context, locked, onSaved };
    const blocked = disabled || locked || serverLocked || !ready || conflict || deleted;
    const persist = () => {
      if (!current()) return;
      try {
        workingDraftStorage().setItem(key, JSON.stringify(draft.current));
      } catch {
        if (mounted.current) setCacheFailed(true);
      }
    };
    const flush = async (createForPhoto = false): Promise<string | null> => {
      if (!current()) throw new Error('The signed-in learner changed. Reopen this material.');
      if (inFlight.current) return inFlight.current;
      if (!loaded.current || conflict)
        throw new Error('Working has not loaded or has a version conflict.');
      if (propsRef.current.locked || serverLocked || deleted)
        return draft.current.version ? draft.current.id : null;
      if (createForPhoto && !draft.current.version) draft.current.dirty = true;
      const operation = async () => {
        while (draft.current.dirty || draft.current.pending) {
          if (!current()) throw new Error('The signed-in learner changed. Reopen this material.');
          const state = draft.current;
          // Keep this exact payload and request id until its outcome is known.
          const pending = state.pending ?? {
            context: propsRef.current.context,
            content: state.content,
            expected_version: state.version,
            request_id: crypto.randomUUID(),
          };
          state.pending = pending;
          persist();
          if (mounted.current) setStatus('Saving…');
          try {
            const saved = await api<WorkingRow>(path(state.id), {
              method: 'PUT',
              body: pending,
              signal: requests.current.signal,
            });
            if (!current()) throw new Error('The signed-in learner changed. Reopen this material.');
            state.version = saved.version;
            state.pending = null;
            state.dirty = JSON.stringify(state.content) !== JSON.stringify(pending.content);
            persist();
            propsRef.current.onSaved?.(state.id);
            if (mounted.current) {
              setServerLocked(saved.locked);
              setStatus(isLocalStudyExperience() ? 'Saved in this experience' : 'Saved');
            }
          } catch (error) {
            if (mounted.current) {
              const code = error instanceof ApiError ? error.code : '';
              if (
                code === 'WORKING_CANVAS_VERSION_CHANGED' ||
                code === 'WORKING_CANVAS_REQUEST_REUSED'
              ) {
                setConflict(true);
                setStatus('Another device changed this page. Export your draft, then reload.');
              } else if (code === 'WORKING_CANVAS_LOCKED') {
                setServerLocked(true);
                setStatus('Submitted · changes were not saved');
              } else if (code === 'WORKING_CANVAS_RETENTION_REQUIRED')
                setStatus(
                  'Cloud saving awaits an approved retention policy. Draft stays on this device.',
                );
              else setStatus('Not synced · draft on this device. Reconnect, then retry.');
            }
            throw error;
          }
        }
        return draft.current.version ? draft.current.id : null;
      };
      inFlight.current = operation().finally(() => {
        inFlight.current = null;
      });
      return inFlight.current;
    };
    const flushRef = useRef(flush);
    flushRef.current = flush;
    useImperativeHandle(
      ref,
      () => ({
        flush: () => flushRef.current(),
        hasUnsavedChanges: () => Boolean(draft.current.dirty || draft.current.pending),
      }),
      [],
    );

    useEffect(() => {
      mounted.current = true;
      const controller = new AbortController();
      requests.current = controller;
      const unsubscribe = useAuthStore.subscribe(() => {
        if (getKidSub() !== owner) controller.abort();
      });
      let cancelled = false;
      const load = async () => {
        let cached: Draft | null = null;
        try {
          const raw: unknown = JSON.parse(workingDraftStorage().getItem(key) ?? 'null');
          if (
            raw &&
            typeof raw === 'object' &&
            'content' in raw &&
            'id' in raw &&
            'version' in raw &&
            'dirty' in raw &&
            'pending' in raw &&
            typeof raw.id === 'string' &&
            /^[0-9a-f-]{36}$/i.test(raw.id) &&
            typeof raw.version === 'number'
          ) {
            const parsed = workingContentSchema.safeParse(raw.content);
            const pending = pendingSchema.nullable().safeParse(raw.pending);
            if (
              parsed.success &&
              pending.success &&
              Number.isInteger(raw.version) &&
              raw.version >= 0 &&
              (!pending.data ||
                (pending.data.context.kind === context.kind &&
                  pending.data.context.question_id === context.question_id &&
                  pending.data.context.session_id === context.session_id))
            )
              cached = {
                id: raw.id,
                version: raw.version,
                content: parsed.data,
                dirty: Boolean(raw.dirty),
                pending: pending.data,
              };
          }
        } catch {
          /* Storage can be unavailable; cloud load still runs. */
        }
        try {
          const query = new URLSearchParams({
            kind: context.kind,
            question_id: context.question_id,
            ...(context.session_id ? { session_id: context.session_id } : {}),
          });
          const row = await api<WorkingRow | null>(
            savedPageId ? path(savedPageId) : `/study/working-canvases/draft?${query}`,
            { signal: controller.signal },
          );
          if (cancelled || !current()) return;
          if (row?.locked || locked) {
            if (cached?.dirty || cached?.pending) setRecovery(cached.content);
            draft.current = row
              ? {
                  id: row.id,
                  version: row.version,
                  content: row.content,
                  dirty: false,
                  pending: null,
                }
              : { ...draft.current, content: blankWorking() };
            setServerLocked(true);
            setStatus('Submitted · read only');
          } else if (cached?.dirty || cached?.pending) {
            draft.current = cached;
            if (
              row &&
              (row.id !== cached.id || (row.version !== cached.version && !cached.pending))
            ) {
              setConflict(true);
              setStatus('Another device changed this page. Export your draft, then reload.');
            } else setStatus('Restored device draft · syncing');
          } else if (row) {
            draft.current = {
              id: row.id,
              version: row.version,
              content: row.content,
              dirty: false,
              pending: null,
            };
            setStatus(isLocalStudyExperience() ? 'Saved in this experience' : 'Saved');
          } else {
            setStatus('Ready to write');
          }
        } catch (error) {
          if (cancelled || !current()) return;
          if (savedPageId && error instanceof ApiError && error.status === 404) {
            setStatus('This saved page expired or was deleted. Return to My working.');
            return;
          }
          if (cached && !locked) {
            draft.current = cached;
            setStatus('Offline · restored device draft');
          } else if (locked || savedPageId) {
            setStatus('Saved working could not load. Reconnect and reload.');
            return;
          } else setStatus('Offline · new device draft');
        }
        loaded.current = true;
        setReady(true);
        setContent(draft.current.content);
        if (draft.current.version) onSaved?.(draft.current.id);
      };
      void load();
      const online = () => {
        void flushRef.current().catch(() => undefined);
      };
      window.addEventListener('online', online);
      const leaving = (event: BeforeUnloadEvent) => {
        if (draft.current.dirty || draft.current.pending) {
          event.preventDefault();
        }
      };
      window.addEventListener('beforeunload', leaving);
      return () => {
        cancelled = true;
        mounted.current = false;
        controller.abort();
        unsubscribe();
        window.removeEventListener('online', online);
        window.removeEventListener('beforeunload', leaving);
      };
      // Context is immutable for the keyed panel. Callback updates are read through propsRef.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
    useEffect(() => {
      if (blocked || !draft.current.dirty) return;
      const timer = window.setTimeout(() => {
        void flushRef.current().catch(() => undefined);
      }, 700);
      return () => window.clearTimeout(timer);
    }, [content, blocked]);
    useEffect(() => {
      if (
        context.kind !== 'material' ||
        !ready ||
        !question ||
        blocked ||
        draft.current.content.question === question
      )
        return;
      draft.current.content = { ...draft.current.content, question };
      draft.current.dirty = true;
      setContent(draft.current.content);
      persist();
      setStatus('Draft on this device');
      // Context is immutable; question selection is saved in the same working version.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [question, ready, blocked, context.kind]);
    const reloadSaved = async () => {
      if (!current()) return;
      if (!confirmReload) {
        setConfirmReload(true);
        return;
      }
      try {
        if (inFlight.current) await inFlight.current.catch(() => undefined);
        const query = new URLSearchParams({
          kind: context.kind,
          question_id: context.question_id,
          ...(context.session_id ? { session_id: context.session_id } : {}),
        });
        const row = await api<WorkingRow | null>(
          savedPageId ? path(savedPageId) : `/study/working-canvases/draft?${query}`,
          { signal: requests.current.signal },
        );
        if (!current()) return;
        draft.current = row
          ? { id: row.id, version: row.version, content: row.content, dirty: false, pending: null }
          : {
              id: crypto.randomUUID(),
              version: 0,
              content: blankWorking(),
              dirty: false,
              pending: null,
            };
        persist();
        setContent(draft.current.content);
        setConflict(false);
        setConfirmReload(false);
        setServerLocked(row?.locked ?? false);
        setStatus(row ? 'Saved' : 'Ready to write');
      } catch {
        setStatus('Saved version could not load. Your device draft is still here.');
      }
    };
    const remove = async () => {
      if (!current()) return;
      try {
        if (inFlight.current) await inFlight.current;
        if (draft.current.pending) await flushRef.current();
        if (!current()) return;
        if (draft.current.version)
          await api(path(draft.current.id), {
            method: 'DELETE',
            headers: { 'If-Match': String(draft.current.version) },
            signal: requests.current.signal,
          });
        workingDraftStorage().removeItem(key);
        draft.current = {
          id: crypto.randomUUID(),
          version: 0,
          content: blankWorking(),
          dirty: false,
          pending: null,
        };
        setDeleted(true);
        forgetWorkingTutorReturn(owner, `working:${draft.current.id}`);
        conversation.setTurns([]);
        setRecovery(null);
        setContent(blankWorking());
        setStatus('Working deleted');
      } catch {
        setStatus('Deletion did not complete. Reconnect and retry.');
      }
    };
    const management = (
      <>
        <button
          type="button"
          disabled={!ready || conflict}
          className="mb-3 inline-flex min-h-11 items-center text-sm font-extrabold text-brand-sky"
          onClick={() => {
            if (context.kind === 'free') {
              persist();
              navigate('/learn/study/working');
              return;
            }
            void flushRef
              .current()
              .then(() => navigate('/learn/study/working'))
              .catch(() => undefined);
          }}
        >
          View all my working
        </button>
        {ready && !deleted && (
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-pill-secondary"
              disabled={conflict || locked || serverLocked || disabled}
              onClick={() => void flushRef.current().catch(() => undefined)}
            >
              Sync working now
            </button>
            <button
              type="button"
              className="btn-pill-secondary"
              onClick={() => {
                if (confirmDelete) void remove();
                else setConfirmDelete(true);
              }}
            >
              {confirmDelete ? 'Confirm deletion' : 'Delete my working'}
            </button>
            {confirmDelete && (
              <button
                type="button"
                className="btn-pill-secondary"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel deletion
              </button>
            )}
          </div>
        )}
      </>
    );
    return (
      <div
        className={embedded ? 'flex h-full min-h-0 flex-col' : undefined}
        data-study-tool="working-canvas"
        data-study-semantic="canvas"
      >
        <MathWorkingCanvas
          embedded={embedded}
          value={content}
          startOpen={startOpen}
          startWithTutor={startWithTutor}
          question={
            context.kind === 'free' || context.kind === 'material' ? content.question : question
          }
          onQuestionChange={
            context.kind === 'free'
              ? (question) => {
                  draft.current.content = { ...draft.current.content, question };
                  draft.current.dirty = true;
                  setContent(draft.current.content);
                  persist();
                  setStatus('Draft on this device');
                }
              : undefined
          }
          contextLabel={contextLabel}
          locked={locked || serverLocked}
          disabled={blocked}
          status={status}
          management={management}
          tutor={
            context.kind !== 'material' ? (
              <WorkingTutorPanel
                conversation={conversation}
                content={content}
                disabled={disabled || !ready || conflict || deleted}
                assessment={context.kind === 'mock' && !(locked || serverLocked)}
                flush={() =>
                  flushRef.current(context.kind === 'free' || conversation.uploads.length > 0)
                }
              />
            ) : undefined
          }
          onChange={(next) => {
            recordHostedInteraction('working-canvas', 'canvas_edited');
            draft.current.content = next;
            draft.current.dirty = true;
            setContent(next);
            persist();
            setStatus('Draft on this device');
          }}
        />
        {conflict && (
          <div className="mt-2">
            <p className="text-xs text-slate2">
              Export your device draft first. Loading the saved page replaces this device draft.
            </p>
            <button
              type="button"
              className="btn-pill-secondary mt-2"
              onClick={() => void reloadSaved()}
            >
              {confirmReload ? 'Confirm replace with saved page' : 'Load saved page'}
            </button>
          </div>
        )}
        {recovery && (
          <details className="mt-2">
            <summary className="cursor-pointer text-sm font-bold text-ink">
              Unsynced device draft · export a copy
            </summary>
            <p className="mt-2 text-xs text-slate2">
              These changes were not accepted before submission. They are separate from the
              submitted response.
            </p>
            <MathWorkingCanvas
              value={recovery}
              onChange={() => undefined}
              locked
              status="Unsynced device draft · read only"
            />
          </details>
        )}
        {cacheFailed && (
          <p role="alert" className="mt-2 text-sm text-danger">
            Device storage is unavailable. Keep this page open until cloud saving succeeds.
          </p>
        )}
        {embedded && ready && !deleted && (
          <button
            type="button"
            className="btn-pill-secondary mx-4 my-2 shrink-0 self-start"
            disabled={blocked}
            onClick={() => void flushRef.current().catch(() => undefined)}
          >
            Sync working now
          </button>
        )}
        {!embedded && management}
      </div>
    );
  },
);
